import { useQuery } from "@tanstack/react-query";
import invariant from "tiny-invariant";

import moment from "moment-timezone";
import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";
import { useFamilyConstants } from "../use-families";
import { useTimezoneConverter } from "../use-timezone-converter";
import { inquiryQueryKeys } from "./query-keys";
import type { InquiryDashboardResponsePayload } from "./types";

interface UseInquiryDashboardProps {
  centerId: string;
  inquiryId?: string;
}

const formatDashboardData = (
  {
    student,
    service,
    source,
    familyInfo,
    upcomingEvents,
    pastEvents
  }: Pick<
    InquiryDashboardResponsePayload,
    "student" | "service" | "source" | "familyInfo" | "upcomingEvents" | "pastEvents"
  >,
  centerTimezone: string,
  EMERGENCY_CONTACT_VALUE: string,
) => {
  const convertEvent = (event: (typeof upcomingEvents)[number]) => ({
    ...event,
    scheduledDateTime: moment.tz(event.scheduledDateTime, centerTimezone).format(),
    ...(event.endDateTime && { endDateTime: moment.tz(event.endDateTime, centerTimezone).format() })
  });

  const attendees = familyInfo.map(({ id, displayName }) => ({ value: id, label: displayName })) ?? [];
  attendees.push({ value: student.id, label: student.displayName });
  attendees.sort((left, right) => left.label.localeCompare(right.label));
  return {
    inquiry: [
      {
        studentId: student.id,
        studentName: student.displayName,
        grade: student.grade,
        service: service,
        source: source,
        gender: student.gender,
        school_name: student.schoolName
      }
    ],
    contacts: familyInfo
      .filter(
        ({ isPrimaryContact, subjectRelationships }) =>
          isPrimaryContact ||
          subjectRelationships.some(
            subjectRelationship => subjectRelationship.relationship.value === EMERGENCY_CONTACT_VALUE
          )
      )
      .map(familyMemberInfo => {
        const isEmergencyContact = familyMemberInfo.subjectRelationships.some(
          subjectRelationship => subjectRelationship.relationship.value === EMERGENCY_CONTACT_VALUE
        );

        return {
          id: familyMemberInfo.id,
          displayName: familyMemberInfo.displayName,
          email: familyMemberInfo.email,
          preferredPhone: familyMemberInfo.preferredPhone.phoneNumber,
          source: source,
          contactType:
            familyMemberInfo.isPrimaryContact && isEmergencyContact
              ? "Primary/Emergency"
              : familyMemberInfo.isPrimaryContact
                ? "Primary"
                : isEmergencyContact
                  ? "Emergency"
                  : "",
          relation: familyMemberInfo.subjectRelationships.find(
            ({ relationship: { value } }) => value !== EMERGENCY_CONTACT_VALUE
          )?.relationship.name
        };
      }),
    upcomingEvents: upcomingEvents.map(convertEvent),
    pastEvents: pastEvents.map(convertEvent),
    familyInfo,
    student,
    attendees
  };
};

export type InquiryDashboard = ReturnType<typeof formatDashboardData>;
export type DashboardInquiry = InquiryDashboard["inquiry"][number];
export type DashboardContact = InquiryDashboard["contacts"][number];
export type DashboardUpcomingEvent = InquiryDashboard["upcomingEvents"][number];

export const useInquiryDashboard = ({ centerId, inquiryId }: UseInquiryDashboardProps) => {
  const { getToken } = useAccessToken();
  const { EMERGENCY_CONTACT_VALUE } = useFamilyConstants();
  const { timezone } = useTimezoneConverter(centerId);

  return useQuery({
    queryKey: inquiryQueryKeys.dashboard(centerId, inquiryId),
    enabled: Boolean(centerId.length && inquiryId?.length),
    queryFn: async () => {
      invariant(inquiryId, "centerId is required");
      return getInquiryDashboard(await getToken(), centerId, inquiryId);
    },
    select: data => formatDashboardData(data, timezone, EMERGENCY_CONTACT_VALUE)
  });
};

export const getInquiryDashboard = async (accessToken: string, centerId: string, inquiryId: string) => {
  return callLcosService<InquiryDashboardResponsePayload>(
    accessToken,
    `/api/inquiries/${centerId}/${inquiryId}/dashboard`,
    "GET"
  );
};
